import { useEffect, useCallback, useState } from 'react';
import { supabase } from '@/lib/supabase';
import type { RealtimeChannel, RealtimePostgresChangesPayload } from '@supabase/supabase-js';

interface PartnerConnection {
  id: string;
  pregnancy_id: string;
  user_1_id: string;
  user_2_id: string;
  status: 'pending' | 'active' | 'removed' | 'pregnancy_deleted';
  created_at: string;
}

interface UseRealtimeSyncOptions {
  pregnancyId: string | null;
  userId: string | null;
  onPartnerConnected?: (connection: PartnerConnection) => void;
  onPartnerDisconnected?: () => void;
  onPartnerUpdated?: (connection: PartnerConnection) => void;
}

export function useRealtimeSync({
  pregnancyId,
  userId,
  onPartnerConnected,
  onPartnerDisconnected,
  onPartnerUpdated,
}: UseRealtimeSyncOptions) {
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [channel, setChannel] = useState<RealtimeChannel | null>(null);

  // Handle realtime changes
  const handlePartnerChange = useCallback(
    (payload: RealtimePostgresChangesPayload<PartnerConnection>) => {
      const { eventType, new: newRecord, old: oldRecord } = payload;

      switch (eventType) {
        case 'INSERT':
          if (newRecord && newRecord.status === 'active') {
            onPartnerConnected?.(newRecord as PartnerConnection);
          }
          break;

        case 'UPDATE':
          if (newRecord) {
            const record = newRecord as PartnerConnection;
            if (record.status === 'active' && oldRecord?.status === 'pending') {
              onPartnerConnected?.(record);
            } else if (record.status === 'removed') {
              onPartnerDisconnected?.();
            } else {
              onPartnerUpdated?.(record);
            }
          }
          break;

        case 'DELETE':
          onPartnerDisconnected?.();
          break;
      }
    },
    [onPartnerConnected, onPartnerDisconnected, onPartnerUpdated]
  );

  // Subscribe to realtime changes
  useEffect(() => {
    if (!pregnancyId || !userId) {
      return;
    }

    // Create channel for partner connections
    const realtimeChannel = supabase
      .channel(`partner-sync:${pregnancyId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'partner_connections',
          filter: `pregnancy_id=eq.${pregnancyId}`,
        },
        handlePartnerChange
      )
      .subscribe((status: string) => {
        setIsSubscribed(status === 'SUBSCRIBED');
      });

    setChannel(realtimeChannel);

    // Cleanup on unmount
    return () => {
      realtimeChannel.unsubscribe();
      setIsSubscribed(false);
      setChannel(null);
    };
  }, [pregnancyId, userId, handlePartnerChange]);

  // Manual unsubscribe function
  const unsubscribe = useCallback(() => {
    if (channel) {
      channel.unsubscribe();
      setIsSubscribed(false);
      setChannel(null);
    }
  }, [channel]);

  return {
    isSubscribed,
    unsubscribe,
  };
}

// Hook for subscribing to specific data changes (e.g., names, checklists)
interface UseRealtimeTableOptions<T> {
  table: string;
  filter?: string;
  enabled?: boolean;
  onInsert?: (record: T) => void;
  onUpdate?: (record: T, oldRecord: T | null) => void;
  onDelete?: (record: T) => void;
}

export function useRealtimeTable<T extends Record<string, unknown>>({
  table,
  filter,
  enabled = true,
  onInsert,
  onUpdate,
  onDelete,
}: UseRealtimeTableOptions<T>) {
  const [isSubscribed, setIsSubscribed] = useState(false);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const channelConfig: {
      event: '*';
      schema: 'public';
      table: string;
      filter?: string;
    } = {
      event: '*',
      schema: 'public',
      table,
    };

    if (filter) {
      channelConfig.filter = filter;
    }

    const channel = supabase
      .channel(`table-sync:${table}:${filter || 'all'}`)
      .on('postgres_changes', channelConfig, (payload: RealtimePostgresChangesPayload<T>) => {
        const { eventType, new: newRecord, old: oldRecord } = payload;

        switch (eventType) {
          case 'INSERT':
            if (newRecord) {
              onInsert?.(newRecord as T);
            }
            break;

          case 'UPDATE':
            if (newRecord) {
              onUpdate?.(newRecord as T, (oldRecord as T) || null);
            }
            break;

          case 'DELETE':
            if (oldRecord) {
              onDelete?.(oldRecord as T);
            }
            break;
        }
      })
      .subscribe((status: string) => {
        setIsSubscribed(status === 'SUBSCRIBED');
      });

    return () => {
      channel.unsubscribe();
      setIsSubscribed(false);
    };
  }, [table, filter, enabled, onInsert, onUpdate, onDelete]);

  return { isSubscribed };
}
